import { InventoryAuthority, RunCollectionAiResult } from '@qkindex/contracts';
import { getCollectionAiCapability } from '../firebase/functions';
import { imageOperationService } from './imageOperationService';

export type ImageAnalysisAuthority = InventoryAuthority & {
  membershipVersion: number;
};
export type ImageAnalysisSource =
  | { kind: 'saved-item'; itemId: string; skipSavedAnalysis?: boolean }
  | { kind: 'received-bytes'; image: Blob; skipSavedAnalysis?: boolean };
export interface ImageAnalysisIntent {
  operationId: string;
  costVersion: string;
  maxCredits?: number;
  expectedSourceHash?: string;
  expectedSourceGeneration?: string;
}

export const mergeImageKeywords = (
  existing: string,
  suggested: string,
): string => {
  const values =
    existing.trim().toLowerCase() === 'unnamed' ? [] : existing.split(/[,\n]+/);
  const seen = new Set(values.map((value) => value.trim().toLowerCase()));
  for (const raw of suggested.split(/[,\n]+/)) {
    const value = raw.trim();
    if (value && !seen.has(value.toLowerCase())) {
      values.push(value);
      seen.add(value.toLowerCase());
    }
  }
  return values
    .map((value) => value.trim())
    .filter(Boolean)
    .join(', ');
};

export const imageAnalysisService = {
  async capability(authority: ImageAnalysisAuthority) {
    return (
      await getCollectionAiCapability({
        collectionId: authority.collectionId,
        expectedMembershipVersion: authority.membershipVersion,
        workflow: 'image-analysis',
        feature: 'image-analysis',
        imageCount: 1,
      })
    ).data;
  },
  async generate(
    authority: ImageAnalysisAuthority,
    source: ImageAnalysisSource,
    intent: ImageAnalysisIntent,
  ): Promise<RunCollectionAiResult> {
    if (authority.compatibilityMode !== 'collection-only')
      throw new Error('Collection authority is required.');
    return imageOperationService.submit<RunCollectionAiResult>(
      {
        action: 'collection-ai',
        collectionId: authority.collectionId,
        expectedMembershipVersion: authority.membershipVersion,
        operationId: intent.operationId,
        workflow: 'image-analysis',
        feature: 'image-analysis',
        imageCount: 1,
        acceptedCostVersion: intent.costVersion,
        ...(intent.maxCredits === undefined
          ? {}
          : { maxCredits: intent.maxCredits }),
        ...(intent.expectedSourceHash === undefined
          ? {}
          : { expectedSourceHash: intent.expectedSourceHash }),
        ...(intent.expectedSourceGeneration === undefined
          ? {}
          : { expectedSourceGeneration: intent.expectedSourceGeneration }),
        sourceKind: source.kind,
        ...(source.skipSavedAnalysis ? { skipSavedAnalysis: true } : {}),
        ...(source.kind === 'saved-item'
          ? { sourceItemId: source.itemId }
          : {}),
      },
      source.kind === 'received-bytes' ? source.image : undefined,
    );
  },
};
